import {
  ILinkDataStorage,
  ILocalSongDataStorage,
  IRecordAudioStorage,
  ISelectedTabStorage,
} from "@/services/storage/base/storage-abstractions";
import { LinkDataStorageMixin } from "@/services/storage/storage-mixins/link-data-storage-mixin";
import { LocalSongDataStorageMixin } from "@/services/storage/storage-mixins/local-song-data-storage-mixin";
import { SelectedTabStorageMixin } from "@/services/storage/storage-mixins/selected-tab-storage-mixin";
import { RecordAudioStorageMixin } from "@/services/storage/storage-mixins/record-audio-storage-mixin";
import {
  LocalFileSongData,
  RecordAudioSongData,
  UploadFileTab,
  YouTubeSongData,
} from "@/providers/media-input-provider/media-input-provider";

export type SelectedFileTab = "youtube" | "localFile" | "recordAudio";

export abstract class MediaInputStorage
  implements
    ILocalSongDataStorage,
    ILinkDataStorage,
    IRecordAudioStorage,
    ISelectedTabStorage<SelectedFileTab>
{
  protected storeName: string;
  protected databaseName: string;

  linkDataStorage: LinkDataStorageMixin;
  localSongDataStorage: LocalSongDataStorageMixin;
  selectedTabStorage: SelectedTabStorageMixin<SelectedFileTab>;
  recordAudioStorage: RecordAudioStorageMixin;

  protected constructor(storeName: string, databaseName: string) {
    this.storeName = storeName;
    this.databaseName = databaseName;

    this.linkDataStorage = new LinkDataStorageMixin(
      this.storeName,
      this.databaseName,
    );
    this.localSongDataStorage = new LocalSongDataStorageMixin(
      this.storeName,
      this.databaseName,
    );
    this.selectedTabStorage = new SelectedTabStorageMixin<SelectedFileTab>(
      this.storeName,
      this.databaseName,
    );
    this.recordAudioStorage = new RecordAudioStorageMixin(
      this.storeName,
      this.databaseName,
    );
  }

  async getMediaInputsFromStorage() {
    const youtubeStorageData = await this.linkDataStorage.getLinkData();
    const youtubeData =
      youtubeStorageData === null
        ? undefined
        : ({
            ...youtubeStorageData,
            type: "youtube",
          } as YouTubeSongData);

    const selectedTab =
      (await this.selectedTabStorage.getSelectedFileTab<UploadFileTab>()) ??
      "youtube";

    const localSongStorageData =
      await this.localSongDataStorage.getLocalSongData();
    const localSongData =
      localSongStorageData === null
        ? undefined
        : ({
            ...localSongStorageData,
            type: "localFile",
          } as LocalFileSongData);

    const recordAudioStorageData =
      await this.recordAudioStorage.getRecordAudioData();
    const recordAudioSongData =
      recordAudioStorageData === null
        ? undefined
        : ({
            ...recordAudioStorageData,
            type: "recordAudio",
          } as RecordAudioSongData);

    return {
      activeTab: selectedTab,
      youtube: youtubeData,
      localFile: localSongData,
      recordAudio: recordAudioSongData,
    };
  }
}
